import * as Popover from "@radix-ui/react-popover";
import { AnimatePresence, motion } from "framer-motion";
import * as React from "react";
import { cn } from "src/lib/cn";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { keepFocusInEditorIfEventFromEditor } from "src/components/TiptapDocumentSB/helpers";

// ---- Context for closing the menu from items ----

type MenuPopoverContextValue = {
  close: () => void;
  isOpen: boolean;
  openedViaKeyboardRef: React.MutableRefObject<boolean>;
};

const defaultKeyboardRef = { current: false };

const MenuPopoverContext = React.createContext<MenuPopoverContextValue>({
  close: () => {},
  isOpen: false,
  openedViaKeyboardRef: defaultKeyboardRef,
});

// ---- Shared item styles (matching WithContextMenu) ----

const menuItemBaseStyles =
  "text-app-grey-0 hover:bg-app-menu-color-hover focus:bg-app-menu-color-hover hover:text-app-grey-0 focus:text-app-grey-0 data-[variant=destructive]:text-app-danger-color data-[variant=destructive]:hover:bg-app-menu-color-hover data-[variant=destructive]:hover:text-app-danger-color data-[variant=destructive]:focus:bg-app-menu-color-hover data-[variant=destructive]:focus:text-app-danger-color [&_path]:fill-current relative flex cursor-pointer items-center gap-1 py-[9.5px] px-[12px] text-base font-inter font-medium select-none transition-all data-[disabled]:pointer-events-none data-[disabled]:opacity-30 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-auto outline-none focus-visible:outline-2 focus-visible:outline-[var(--primary-cta)] focus-visible:-outline-offset-2 first:rounded-t-[10px] last:rounded-b-[10px] only:rounded-t-[10px] only:rounded-b-[10px]";

// ---- MenuPopover (Root) ----

// Module-level set of close callbacks so only one MenuPopover is open at a time.
const openMenuCloseCallbacks = new Set<() => void>();

interface MenuPopoverProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
}

function MenuPopover({ children, open, onOpenChange, modal = true }: MenuPopoverProps) {
  const [isOpen, setIsOpen] = React.useState(open ?? false);
  const openedViaKeyboardRef = React.useRef(false);

  React.useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open);
    }
  }, [open]);

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (open === undefined) {
        setIsOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [open, onOpenChange],
  );

  // When this menu opens, close all other open menus
  React.useEffect(() => {
    if (isOpen) {
      openMenuCloseCallbacks.forEach((cb) => cb());
      openMenuCloseCallbacks.clear();

      const closeSelf = () => handleOpenChange(false);
      openMenuCloseCallbacks.add(closeSelf);

      return () => {
        openMenuCloseCallbacks.delete(closeSelf);
      };
    }
  }, [isOpen, handleOpenChange]);

  const contextValue = React.useMemo(() => ({ close: () => handleOpenChange(false), isOpen, openedViaKeyboardRef }), [handleOpenChange, isOpen]);

  return (
    <MenuPopoverContext.Provider value={contextValue}>
      <Popover.Root
        data-slot="menu-popover"
        open={isOpen}
        onOpenChange={handleOpenChange}
        modal={modal}
      >
        {children}
      </Popover.Root>
    </MenuPopoverContext.Provider>
  );
}

// ---- MenuPopoverTrigger ----

const MenuPopoverTrigger = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Popover.Trigger>>(({ className, onKeyDown, onPointerDown, ...props }, ref) => {
  const { openedViaKeyboardRef } = React.useContext(MenuPopoverContext);

  // Ensure Enter/Space opens the popover even when the child is not a native <button>
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openedViaKeyboardRef.current = true;
        e.currentTarget.click();
      }
      onKeyDown?.(e);
    },
    [onKeyDown, openedViaKeyboardRef],
  );

  const handlePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      openedViaKeyboardRef.current = false;
      onPointerDown?.(e);
    },
    [onPointerDown, openedViaKeyboardRef],
  );

  return (
    <Popover.Trigger
      ref={ref}
      data-slot="menu-popover-trigger"
      asChild
      aria-haspopup="menu"
      className={className}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      {...props}
    />
  );
});

MenuPopoverTrigger.displayName = "MenuPopoverTrigger";

// ---- MenuPopoverContent ----

type Side = "bottom" | "top" | "right" | "left";
type Align = "start" | "center" | "end";

interface MenuPopoverContentProps {
  children: React.ReactNode;
  /** Additional className for the positioned popover content. */
  className?: string;
  /** Additional className for the outer portal wrapper. Use for stacking context overrides only. */
  portalClassName?: string;
  side?: Side;
  align?: Align;
  sideOffset?: number;
  alignOffset?: number;
  collisionPadding?: number | Partial<Record<Side, number>>;
  "aria-label"?: string;
  animated?: boolean;
  showArrow?: boolean;
  arrowClassName?: string;
  /** Additional className for the inner menu content wrapper (has p-2 by default) */
  contentClassName?: string;
}

function MenuPopoverContent({
  children,
  className,
  portalClassName,
  side = "bottom",
  align = "start",
  sideOffset = 5,
  alignOffset = 0,
  collisionPadding,
  "aria-label": ariaLabel,
  animated = true,
  showArrow = false,
  arrowClassName,
  contentClassName,
}: MenuPopoverContentProps) {
  const contentRef = React.useRef<HTMLDivElement>(null);

  const getItems = React.useCallback(() => {
    if (!contentRef.current) return [];
    return Array.from(contentRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])'));
  }, []);

  const focusItem = React.useCallback((item: HTMLElement | undefined) => {
    item?.focus();
  }, []);

  const { isOpen, openedViaKeyboardRef } = React.useContext(MenuPopoverContext);

  const handleAutoFocus = React.useCallback(
    (e: Event) => {
      e.preventDefault();
      // Only auto-focus the first item when opened via keyboard, not mouse
      if (!openedViaKeyboardRef.current) return;
      openedViaKeyboardRef.current = false;
      // Use a longer delay to ensure framer-motion animation (150ms) completes
      // before attempting to focus the first menu item
      setTimeout(() => {
        const items = getItems();
        focusItem(items[0]);
      }, 160);
    },
    [getItems, focusItem, openedViaKeyboardRef],
  );

  const handleCloseAutoFocus = React.useCallback((e: Event) => {
    keepFocusInEditorIfEventFromEditor(e);
  }, []);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      // Stop keydown from bubbling through React's portal event system
      // to ancestors in the React tree (e.g. document list items)
      e.stopPropagation();

      const items = getItems();
      if (items.length === 0) return;

      const currentIndex = items.indexOf(document.activeElement as HTMLElement);

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
          focusItem(items[nextIndex]);
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
          focusItem(items[prevIndex]);
          break;
        }
        case "Home": {
          e.preventDefault();
          focusItem(items[0]);
          break;
        }
        case "End": {
          e.preventDefault();
          focusItem(items[items.length - 1]);
          break;
        }
      }
    },
    [getItems, focusItem],
  );

  // Stop click events from bubbling through React's portal event system
  // to ancestors in the React tree (e.g. document list items)
  const handleClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const menuContent = (
    <div
      ref={contentRef}
      role="menu"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      className={cn("flex flex-col", contentClassName)}
    >
      {children}
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <Popover.Portal forceMount>
          {/* WithReset's identity transform (translate-x-0 etc.) establishes a new stacking
              context, so z-index only escapes to compete against unrelated portals (e.g. a
              Dialog's overlay) if it's set here too — setting it only on Popover.Content below
              would be trapped inside this context and silently ignored. It also needs `relative`:
              WithReset's div is `position: static` by default, and z-index has no effect at all
              on statically positioned elements, so the override would otherwise be a no-op.
              Keep the content className off this wrapper: Radix positions its child out of flow,
              so overflow classes would clip the menu inside this zero-height element. */}
          <WithReset
            data-slot="menu-popover-portal"
            className={cn("relative z-[100]", portalClassName)}
          >
            <Popover.Content
              data-slot="menu-popover-content"
              side={side}
              align={align}
              sideOffset={sideOffset}
              alignOffset={alignOffset}
              collisionPadding={collisionPadding}
              onOpenAutoFocus={handleAutoFocus}
              onCloseAutoFocus={handleCloseAutoFocus}
              className={cn("z-[100] bg-app-menu-color shadow-card", "border border-app-menu-color-hover rounded-xl overflow-hidden", "outline-none", className)}
              asChild
              forceMount
            >
              {animated ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{
                    duration: 0.15,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  {menuContent}
                  {showArrow && <Popover.Arrow className={cn("fill-app-menu-color-hover", arrowClassName)} />}
                </motion.div>
              ) : (
                <div>
                  {menuContent}
                  {showArrow && <Popover.Arrow className={cn("fill-app-menu-color-hover", arrowClassName)} />}
                </div>
              )}
            </Popover.Content>
          </WithReset>
        </Popover.Portal>
      )}
    </AnimatePresence>
  );
}

// ---- MenuPopoverItem ----

interface MenuPopoverItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect"> {
  children: React.ReactNode;
  className?: string;
  onSelect?: () => void;
  disabled?: boolean;
  variant?: "default" | "destructive";
  /** When false, the menu stays open after selecting this item. Default: true. */
  closeOnSelect?: boolean;
}

function MenuPopoverItem({ children, className, onSelect, disabled = false, variant = "default", closeOnSelect = true, ...props }: MenuPopoverItemProps) {
  const { close } = React.useContext(MenuPopoverContext);

  const handleSelect = React.useCallback(() => {
    if (disabled) return;
    onSelect?.();
    if (closeOnSelect) {
      close();
    }
  }, [disabled, onSelect, close, closeOnSelect]);

  return (
    <div
      data-slot="menu-popover-item"
      data-variant={variant}
      role="menuitem"
      tabIndex={disabled ? undefined : -1}
      aria-disabled={disabled || undefined}
      className={cn(menuItemBaseStyles, className)}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleSelect();
        }
      }}
      {...props}
    >
      {children}
    </div>
  );
}

// ---- MenuPopoverSeparator ----

interface MenuPopoverSeparatorProps {
  className?: string;
}

function MenuPopoverSeparator({ className }: MenuPopoverSeparatorProps) {
  return (
    <div
      data-slot="menu-popover-separator"
      role="separator"
      className={cn("bg-app-divider -mx-2 my-2 h-px", className)}
    />
  );
}

// ---- MenuPopoverLabel ----

interface MenuPopoverLabelProps {
  children: React.ReactNode;
  className?: string;
}

function MenuPopoverLabel({ children, className }: MenuPopoverLabelProps) {
  return (
    <div
      data-slot="menu-popover-label"
      className={cn("text-app-grey-1 px-3 py-1.5 text-sm font-inter text-left font-medium", className)}
    >
      {children}
    </div>
  );
}

// ---- Exports ----

export { MenuPopover, MenuPopoverContent, MenuPopoverItem, MenuPopoverLabel, MenuPopoverSeparator, MenuPopoverTrigger };
